import { Button, Drawer, Modal, Popover, message } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import { GET_AI_SUGGESTION_QUICK_TIP, GET_QUICK_TIPS_PREFERENCE } from "../../../../graphql/query";
import {
  CREATE_AI_SUGGESTION,
  DELETE_AI_SUGGESTION,
  SAVE_QUICK_TIPS_PREFERENCE,
  UPDATE_AI_SUGGESTION,
} from "../../../../graphql/mutation/mutation";
import useApollo from "../../../../hooks/useApollo";
import { ADDING_TYPE } from "../../../adminDashboard/settings/CustomizeAiSuggestions";
import {
  CHANNEL_FILTER,
  getChannelForUseCaseId,
  getUseCaseIdsForChannels,
  normalizeChannels,
  readMutationResult,
} from "../../../../helpers/quickTipsAndTonesHelper";
import CustomItemForm from "./CustomItemForm";
import HideUnhidePanel from "./HideUnhidePanel";
import CustomizePanelContent from "./CustomizePanelContent";
import useAiDraftWriter from "./useAiDraftWriter";
import { createTextareaAdapter } from "./draftAdapters";
import { AI_ACTION, AI_CHANNEL, AI_RESPONSE_COPY, SUGGESTION_TYPE } from "./constants";

// Throws on an in-payload failure. The API answers { statusCode, message } even
// for action-level errors (e.g. 400), so a resolved mutation isn't a success —
// same check the admin Quick Tips & Tones screen makes.
const assertMutationOk = res => {
  const result = readMutationResult(res);
  if (!result.ok) throw new Error(result.message);
};

// Feature flag — user-level custom tips/tones. ON now that the backend exposes
// `userId` on getAiSuggestionQuickTip and scopes custom rows to the auth user.
// Drives the per-item edit/delete menu + 👤 marker.
const USER_CUSTOM_TIPS_ENABLED = true;

// An item is the logged-in user's own custom tip/tone ONLY when the backend
// stamped it with their id. Admin (company/location/department) items come back
// with `userId === null` and are read-only — no more-options menu for them.
const isOwnCustom = (item, userId) =>
  USER_CUSTOM_TIPS_ENABLED && item?.userId != null && String(item.userId) === String(userId);

// Visible unless the item's id is in the saved `hidden` set. Newly created
// items aren't in `hidden`, so they're visible by default.
const isVisible = (item, hidden) => !(hidden || []).includes(item.id);

/**
 * "Customize AI response" controller (EAI-1497 refinement).
 *
 * Owns ALL data + selection logic and renders the SAME `CustomizePanelContent`
 * inside two interchangeable containers:
 *   - a compact anchored **Popover** (default), and
 *   - a right-side **Drawer** (the "maximize" view).
 * State lives here, so maximizing/restoring preserves tips, tone, unsaved
 * changes and the save-preference toggle with no reset.
 *
 * The trigger (composer chevron) is supplied by the host via `renderTrigger`
 * so each surface keeps its own button styling; the Popover anchors to it.
 *
 * @param {{
 *   user: object,
 *   locationId?: number,
 *   applicationUseCaseId?: number,
 *   quickTipVariables: object,
 *   buildResponsePayload: (promptData: object) => object,
 *   text?: object, setInputText?: Function, setLoader?: Function,
 *   value: object, onChange: Function,
 *   emptyTipsText?: string, emptyTonesText?: string,
 *   renderTrigger: (state: { open: boolean }) => React.ReactNode,
 *   onDraftReplaced?: (v: {
 *     source: string, sourceHistoryId: number|null,
 *     result: string, resultHistoryId: number|null,
 *   }) => void,
 *   channel?: string,   // an AI_CHANNEL; defaults to TEXT
 *   adapter?: object,   // see draftAdapters.js; defaults to a textarea adapter
 * }} props
 *
 * EAI-1006: when a generate overwrites text the user already had,
 * `onDraftReplaced` reports the before/after so the host can offer a way back
 * to the earlier version.
 */
export default function CustomizeAiResponse({
  user,
  locationId,
  applicationUseCaseId,
  quickTipVariables,
  buildResponsePayload,
  text,
  setInputText,
  setLoader,
  value,
  onChange,
  emptyTipsText,
  emptyTonesText,
  channelSelectable,
  renderTrigger,
  onDraftReplaced,
  channel,
  adapter,
}) {
  const userId = user?.id;
  const companyId = user?.company?.id;

  // Textarea surfaces (Text composer, review reply) pass `text`/`setInputText`;
  // the rich-text email composer passes its own `adapter` + `channel` instead.
  const { runAiDraft, pendingAction } = useAiDraftWriter({
    channel,
    adapter: adapter || createTextareaAdapter({ text, setInputText }),
    setLoader,
    buildResponsePayload,
    onDraftReplaced,
  });

  const [open, setOpen] = useState(false); // popover
  const [maximized, setMaximized] = useState(false); // drawer
  const [hideMode, setHideMode] = useState(false); // inline hide/unhide view
  const [shownTipIds, setShownTipIds] = useState([]); // hide/unhide draft
  const [shownToneIds, setShownToneIds] = useState([]);
  const [savePref, setSavePref] = useState(false);
  const [form, setForm] = useState(null); // { mode, suggestionType, id, initialValue }
  const [pendingDelete, setPendingDelete] = useState(null); // item awaiting delete confirm
  const [deleting, setDeleting] = useState(false);
  const [savingForm, setSavingForm] = useState(false); // Add/Edit form submit in flight
  const active = open || maximized;

  // Custom tips/tones are scoped to the currently selected opportunity-type tab
  // (Sales / Service / AutoSavvy …), surfaced via quickTipVariables.rootOppTypeId.
  const opportunityTypeId = quickTipVariables?.rootOppTypeId ?? null;

  // Channels, the admin way: a tip's channel is the use case(s) it belongs to.
  // The Add form's channel picker resolves to use case ids, and defaults to the
  // channel of the surface this panel is mounted on (Text composer → Text).
  const { applicationUseCases } = useSelector(state => state.aiData);
  const surfaceChannel =
    getChannelForUseCaseId(applicationUseCases, applicationUseCaseId) || CHANNEL_FILTER.TEXT;
  const channelUseCaseIds = channels => {
    // Text-only surfaces (review reply) have no picker and always write against
    // their own use case.
    if (!channelSelectable) return [Number(applicationUseCaseId)].filter(Boolean);
    const ids = getUseCaseIdsForChannels(applicationUseCases, channels);
    return ids.length ? ids : [Number(applicationUseCaseId)].filter(Boolean);
  };
  // Which channel an existing row belongs to — read from its use case; rows
  // written before the use-case model still carry communicationChannels.
  const editChannels = item => {
    const fromUseCase = getChannelForUseCaseId(applicationUseCases, item?.applicationUseCaseId);
    if (fromUseCase) return [fromUseCase];
    const stored = normalizeChannels(item?.communicationChannels);
    return stored.length ? stored : [surfaceChannel];
  };

  const {
    data: tipsData,
    loading: quickTipLoader,
    refetch: refetchTips,
  } = useQuery(GET_AI_SUGGESTION_QUICK_TIP, { skip: !active, variables: quickTipVariables });
  // Normalize ids to numbers up front: getAiSuggestionQuickTip returns string
  // ids while getQuickTipsPreference (hidden/preserved) returns Int[], so
  // comparisons and the saveQuickTipsPreference payload must all be numeric.
  const allItems = (tipsData?.getAiSuggestionQuickTip || []).map(t => ({ ...t, id: Number(t.id) }));
  const dynamicTips = allItems.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TIPS);
  const dynamicTones = allItems.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TONES);
  const additionalCommentsEnabled = allItems.some(
    t => t?.isActive && t?.suggestionType === ADDING_TYPE.ADDITIONAL_NOTES
  );

  const {
    data: prefData,
    loading: prefLoading,
    refetch: refetchPref,
  } = useQuery(GET_QUICK_TIPS_PREFERENCE, {
    // Both keys are required to read the right bucket — without the use case id
    // the message and review surfaces would share one preference record.
    skip: !active || !locationId || !applicationUseCaseId,
    variables: { locationId, applicationUseCaseId: Number(applicationUseCaseId) },
  });
  const pref = prefData?.getQuickTipsPreference;
  const toNums = arr => (arr || []).map(Number);
  // `hidden` = ids the user chose to hide (empty / absent → nothing hidden).
  const tipHidden = toNums(pref?.quickTips?.hidden);
  const tipPreserved = toNums(pref?.quickTips?.preserved);
  const toneHidden = toNums(pref?.quickTones?.hidden);
  const tonePreserved = toNums(pref?.quickTones?.preserved);

  const [saveQuickTipsPreference, { loading: savingPref }] = useMutation(
    SAVE_QUICK_TIPS_PREFERENCE
  );
  const [createSuggestion] = useMutation(CREATE_AI_SUGGESTION);
  const [updateSuggestion] = useMutation(UPDATE_AI_SUGGESTION);
  const [deleteSuggestion] = useMutation(DELETE_AI_SUGGESTION);
  const { evictField } = useApollo();

  const visibleTips = dynamicTips.filter(t => isVisible(t, tipHidden));
  const visibleTones = dynamicTones.filter(t => isVisible(t, toneHidden));

  // The "Save these preferences" checkbox stays hidden while the current
  // selection still matches the saved defaults (the `preserved` sets that
  // `saveCurrentAsDefault` persists, and that the panel seeds from on open).
  // It only appears once the user changes their tip/tone selection away from
  // those defaults — there is nothing new to save until then.
  const sameIdSet = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every(id => setB.has(id));
  };
  const selectionMatchesDefault =
    sameIdSet(value.quickTipIds, tipPreserved) && sameIdSet(value.toneIds, tonePreserved);
  const showSavePref = !selectionMatchesDefault;

  // If the user reverts back to the defaults, the checkbox disappears — clear a
  // stale "checked" state so a hidden toggle can't silently save on Generate.
  useEffect(() => {
    if (!showSavePref && savePref) setSavePref(false);
  }, [showSavePref, savePref]);

  // Reset the "save" toggle and seed the selection from saved defaults each time
  // the panel opens with an empty selection. Keyed on `active` so switching
  // between popover and drawer does NOT re-seed (state is preserved).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!active) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || prefLoading) return;
    seededRef.current = true;
    setSavePref(false);
    const empty = !value.quickTipIds.length && !value.toneIds.length;
    if (empty && (tipPreserved.length || tonePreserved.length)) {
      onChange({ ...value, quickTipIds: [...tipPreserved], toneIds: [...tonePreserved] });
    }
    // value/onChange intentionally omitted — seed runs once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, prefLoading, tipPreserved, tonePreserved]);

  const closeAll = () => {
    setOpen(false);
    setMaximized(false);
    setHideMode(false);
  };
  const toggleMaximize = () =>
    setMaximized(prev => {
      const next = !prev;
      setOpen(!next); // popover only when not maximized
      return next;
    });

  // Enter the inline hide/unhide view. The panel works in "shown" terms, so seed
  // the shown set as every item that isn't currently hidden.
  const enterHideMode = () => {
    setShownTipIds(dynamicTips.filter(t => !tipHidden.includes(t.id)).map(t => t.id));
    setShownToneIds(dynamicTones.filter(t => !toneHidden.includes(t.id)).map(t => t.id));
    setHideMode(true);
  };
  const toggleShownTip = id =>
    setShownTipIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleShownTone = id =>
    setShownToneIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const toggleTip = item => {
    const has = value.quickTipIds.includes(item.id);
    onChange({
      ...value,
      quickTipIds: has
        ? value.quickTipIds.filter(id => id !== item.id)
        : [...value.quickTipIds, item.id],
    });
  };
  const toggleTone = item =>
    onChange({ ...value, toneIds: value.toneIds.includes(item.id) ? [] : [item.id] });

  // What the AI receives for each selected tip/tone: its description (the
  // instructions written for it) when there is one, otherwise its name.
  const textOf = (ids, list) =>
    list.filter(it => ids.includes(it.id)).map(it => it.description?.trim() || it.tip);

  const persistPreference = ({
    tipHiddenIds,
    toneHiddenIds,
    tipPreservedIds,
    tonePreservedIds,
  }) => {
    // Both scope keys are mandatory server-side; bail with a readable message
    // instead of firing a mutation with a null/NaN scope.
    if (!locationId || !applicationUseCaseId) {
      throw new Error("Couldn't save preferences — no active location.");
    }
    return saveQuickTipsPreference({
      variables: {
        locationId,
        applicationUseCaseId: Number(applicationUseCaseId),
        quickTips: { hidden: toNums(tipHiddenIds), preserved: toNums(tipPreservedIds) },
        quickTones: { hidden: toNums(toneHiddenIds), preserved: toNums(tonePreservedIds) },
      },
    });
  };

  const saveCurrentAsDefault = () =>
    persistPreference({
      // Keep the existing hidden set; only the default selection changes here.
      tipHiddenIds: tipHidden,
      toneHiddenIds: toneHidden,
      tipPreservedIds: value.quickTipIds,
      tonePreservedIds: value.toneIds,
    });

  // Persist the current selection as the default the moment the user ticks
  // "Save these preferences for future responses" — Generate no longer saves.
  // Unchecking is a no-op (there is no un-save). After a successful save the
  // selection matches the refreshed defaults, so `showSavePref` hides the
  // checkbox on its own.
  const handleSavePrefToggle = async checked => {
    setSavePref(checked);
    if (!checked) return;
    try {
      await saveCurrentAsDefault();
      await refetchPref();
      message.success(AI_RESPONSE_COPY.SAVED_TOAST);
    } catch (err) {
      setSavePref(false);
      message.error(err?.message || "Failed to save preferences");
    }
  };

  // Save the hide/unhide selection: the panel gives us the shown sets, so the
  // hidden set is every item NOT shown. Keep the existing `preserved` defaults,
  // then exit the hide/unhide view.
  const handleSaveVisibility = async () => {
    try {
      await persistPreference({
        tipHiddenIds: dynamicTips.filter(t => !shownTipIds.includes(t.id)).map(t => t.id),
        toneHiddenIds: dynamicTones.filter(t => !shownToneIds.includes(t.id)).map(t => t.id),
        tipPreservedIds: tipPreserved,
        tonePreservedIds: tonePreserved,
      });
      await refetchPref();
      setHideMode(false);
      message.success("Visibility updated");
    } catch (err) {
      message.error(err?.message || "Failed to update visibility");
    }
  };

  const openAdd = suggestionType =>
    setForm({ mode: "add", suggestionType, initialValue: { channels: [surfaceChannel] } });
  const openEdit = item =>
    setForm({
      mode: "edit",
      suggestionType: item.suggestionType,
      id: item.id,
      initialValue: {
        tip: item.tip,
        description: item.description,
        channels: editChannels(item),
      },
    });

  // Payloads mirror the admin Quick Tips & Tones screen (QuickTipsAndTones.jsx).
  // The only scope difference: the composer has no department picker, so a
  // custom item is scoped to the active opportunity-type tab (or the location).
  const handleFormSubmit = async values => {
    const { mode, suggestionType } = form || {};
    setSavingForm(true);
    // Set when a create also lands in another channel's list (e.g. a Text + Email
    // tip made from the Text composer).
    let touchedOtherChannel = false;
    try {
      if (mode === "edit") {
        // A row's channel and scope are fixed once created — update carries
        // only the editable fields.
        assertMutationOk(
          await updateSuggestion({
            variables: {
              id: Number(form.id),
              tip: values.tip,
              description: values.description || "",
            },
          })
        );
      } else {
        if (!locationId) throw new Error("Couldn't save — no active location.");
        const applicationUseCaseIds = channelUseCaseIds(values.channels);
        touchedOtherChannel = applicationUseCaseIds.some(id => id !== Number(applicationUseCaseId));
        assertMutationOk(
          await createSuggestion({
            variables: {
              applicationUseCaseIds,
              companyId,
              description: values.description || "",
              isActive: true,
              locationId: Number(locationId),
              suggestionType,
              tip: values.tip,
              departmentId: null,
              opportunityTypeId: opportunityTypeId != null ? Number(opportunityTypeId) : null,
            },
          })
        );
      }
      // Refresh the list so the change shows immediately. Under the `hidden`
      // model a new item isn't in the hidden set, so it's visible by default —
      // no preference write needed.
      if (touchedOtherChannel) {
        // The other channel's list is its own cache entry (different
        // applicationUseCaseId) and its panel is closed, so a plain refetch
        // can't reach it. Drop every cached tips list so the other panel
        // fetches fresh the next time it is opened.
        evictField("getAiSuggestionQuickTip");
      }
      await refetchTips();
      setForm(null);
      message.success(mode === "edit" ? "Updated successfully" : "Added successfully");
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    } finally {
      setSavingForm(false);
    }
  };

  // Delete is a two-step flow: the row menu opens a confirmation (pendingDelete),
  // and only confirming actually calls the mutation.
  const confirmDeleteItem = async () => {
    if (!pendingDelete) return;
    const item = pendingDelete;
    setDeleting(true);
    try {
      assertMutationOk(await deleteSuggestion({ variables: { id: Number(item.id) } }));
      await refetchTips();
      onChange({
        ...value,
        quickTipIds: value.quickTipIds.filter(id => id !== item.id),
        toneIds: value.toneIds.filter(id => id !== item.id),
      });
      message.success("Deleted successfully");
      setPendingDelete(null);
    } catch (err) {
      message.error(err?.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  // The panel always drafts fresh from the selected tips/tone/instructions; it
  // ignores whatever is in the composer. Reworking existing text is the job of
  // the composer's one-click Refine (EAI-1006), not of this panel.
  const handleGenerate = () =>
    runAiDraft({
      action: AI_ACTION.GENERATE,
      promptData: {
        quickTips: textOf(value.quickTipIds, dynamicTips),
        tones: textOf(value.toneIds, dynamicTones),
        additionalNote: value.additionalNote,
      },
      onSettled: closeAll,
    });

  const panel = hideMode ? (
    <HideUnhidePanel
      isMaximized={maximized}
      onToggleMaximize={toggleMaximize}
      onBack={() => setHideMode(false)}
      loading={quickTipLoader}
      tips={dynamicTips}
      tones={dynamicTones}
      shownTipIds={shownTipIds}
      shownToneIds={shownToneIds}
      onToggleTip={toggleShownTip}
      onToggleTone={toggleShownTone}
      onSave={handleSaveVisibility}
      saving={savingPref}
      additionalCommentsEnabled={additionalCommentsEnabled}
    />
  ) : (
    <CustomizePanelContent
      isMaximized={maximized}
      onToggleMaximize={toggleMaximize}
      onOpenHideUnhide={enterHideMode}
      quickTipLoader={quickTipLoader}
      tips={visibleTips}
      tones={visibleTones}
      emptyTipsText={emptyTipsText}
      emptyTonesText={emptyTonesText}
      value={value}
      onToggleTip={toggleTip}
      onToggleTone={toggleTone}
      onChangeNote={note => onChange({ ...value, additionalNote: note })}
      additionalCommentsEnabled={additionalCommentsEnabled}
      showSavePref={showSavePref}
      savePref={savePref}
      onSavePrefChange={handleSavePrefToggle}
      savingPref={savingPref}
      generating={pendingAction === AI_ACTION.GENERATE}
      onGenerate={handleGenerate}
      isOwnCustom={item => isOwnCustom(item, userId)}
      onAddTip={() => openAdd(SUGGESTION_TYPE.TIPS)}
      onAddTone={() => openAdd(SUGGESTION_TYPE.TONES)}
      onEditItem={openEdit}
      onDeleteItem={setPendingDelete}
    />
  );

  const deleteNoun = pendingDelete?.suggestionType === SUGGESTION_TYPE.TONES ? "tone" : "tip";
  const deleteNounCap = deleteNoun.charAt(0).toUpperCase() + deleteNoun.slice(1);
  const deleteBody = AI_RESPONSE_COPY.DELETE_CONFIRM_BODY.replace(
    /\{Noun\}/g,
    deleteNounCap
  ).replace(/\{noun\}/g, deleteNoun);

  return (
    <>
      <Popover
        overlayClassName="ai-prompt-popup"
        trigger="click"
        placement="topRight"
        open={open && !maximized}
        overlayInnerStyle={{ padding: 0, overflow: "hidden", borderRadius: 8 }}
        onOpenChange={next => {
          // Keep the popover mounted while a body-portaled Add/Edit form or the
          // delete-confirm modal is open (their clicks would otherwise read as
          // an outside-click that dismisses the panel).
          if (!next && (!!form || !!pendingDelete)) return;
          setOpen(next);
        }}
        content={
          <div
            style={{ width: "100%", maxHeight: "min(90vh, 720px)" }}
            className="flex flex-col overflow-hidden"
          >
            {panel}
          </div>
        }
      >
        {renderTrigger({ open: open && !maximized })}
      </Popover>

      <Drawer
        open={maximized}
        onClose={() => setMaximized(false)}
        placement="right"
        width={400}
        closable={false}
        styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
      >
        {maximized && panel}
      </Drawer>

      {form && (
        <CustomItemForm
          open={!!form}
          mode={form.mode}
          suggestionType={form.suggestionType}
          initialValue={form.initialValue}
          submitting={savingForm}
          channelSelectable={channelSelectable}
          onSubmit={handleFormSubmit}
          onClose={() => setForm(null)}
        />
      )}

      <Modal
        open={!!pendingDelete}
        title={AI_RESPONSE_COPY.DELETE_CONFIRM_TITLE.replace("{noun}", deleteNoun)}
        onCancel={() => setPendingDelete(null)}
        maskClosable={!deleting}
        width={400}
        zIndex={1100}
        footer={
          <div className="flex items-center gap-2">
            <Button danger type="primary" loading={deleting} onClick={confirmDeleteItem}>
              {AI_RESPONSE_COPY.MENU_DELETE}
            </Button>
            <Button disabled={deleting} onClick={() => setPendingDelete(null)}>
              {AI_RESPONSE_COPY.CANCEL}
            </Button>
          </div>
        }
      >
        {deleteBody}
      </Modal>
    </>
  );
}

CustomizeAiResponse.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    company: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
  }).isRequired,
  locationId: PropTypes.number,
  applicationUseCaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  quickTipVariables: PropTypes.shape({}).isRequired,
  buildResponsePayload: PropTypes.func.isRequired,
  text: PropTypes.shape({}),
  setInputText: PropTypes.func,
  setLoader: PropTypes.func,
  value: PropTypes.shape({
    quickTipIds: PropTypes.arrayOf(PropTypes.number),
    toneIds: PropTypes.arrayOf(PropTypes.number),
    additionalNote: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  emptyTipsText: PropTypes.string,
  emptyTonesText: PropTypes.string,
  channelSelectable: PropTypes.bool,
  renderTrigger: PropTypes.func.isRequired,
  onDraftReplaced: PropTypes.func,
  channel: PropTypes.string,
  adapter: PropTypes.shape({}),
};

CustomizeAiResponse.defaultProps = {
  locationId: undefined,
  applicationUseCaseId: undefined,
  text: undefined,
  setInputText: undefined,
  setLoader: undefined,
  emptyTipsText: AI_RESPONSE_COPY.NO_TIPS,
  emptyTonesText: AI_RESPONSE_COPY.NO_TONES,
  channelSelectable: true,
  onDraftReplaced: undefined,
  channel: AI_CHANNEL.TEXT,
  adapter: undefined,
};
